import { StateMachine } from "@/core/arch/state_machine";
import { isTerminal, VisibilityPoller } from "./job_polling";
import type { JobDetail, JobHistoryRepository } from "./job_history_repository";

export interface JobDetailState {
  /** `missing` is a row that no longer exists, which a `ttl` makes routine. */
  status: "idle" | "loading" | "loaded" | "missing" | "error";
  job: JobDetail | null;
  error?: string;
}

/**
 * Reads one run and, while it is still in flight, keeps it current.
 *
 * Fetches by id rather than reusing the history row so a link pasted from
 * elsewhere resolves even when that run is not on the loaded page.
 */
export class JobDetailBloc extends StateMachine<JobDetailState> {
  private readonly poller = new VisibilityPoller(() => {
    void this.refresh();
  });
  private refreshing = false;
  private disposed = false;

  constructor(
    private readonly repo: JobHistoryRepository,
    private jobId: string,
  ) {
    super({ status: "idle", job: null });
    this.appendDisposeCallback(this.teardown);
  }

  private readonly teardown = (): void => {
    this.disposed = true;
    this.poller.stop();
  };

  /**
   * Returns a disposed bloc to service.
   *
   * `useStateMachine` disposes on effect cleanup but keeps the same instance in
   * its ref, so React StrictMode's mount/cleanup/mount replay hands the view a
   * bloc that has already been torn down. Re-registering matters as much as
   * clearing the flag: `dispose` consumes its callbacks, so without this a
   * later real unmount would no longer stop the poller.
   */
  private rearm(): void {
    this.disposed = false;
    this.appendDisposeCallback(this.teardown);
  }

  async load(jobId: string = this.jobId): Promise<void> {
    if (this.disposed) this.rearm();
    this.jobId = jobId;
    this.poller.stop();
    this.setState({ status: "loading", job: null });
    try {
      const job = await this.repo.getJob(jobId);
      if (jobId !== this.jobId) return;
      this.setState(
        job ? { status: "loaded", job } : { status: "missing", job: null },
      );
    } catch (err) {
      // Guarded like the success path: a failure from a run the sheet has
      // already moved off must not replace the current run with an error.
      if (jobId !== this.jobId) return;
      this.setState({
        status: "error",
        job: null,
        error: err instanceof Error ? err.message : String(err),
      });
    }
    this.syncPolling();
  }

  /** Silent re-read; a failed tick leaves the shown run alone and retries. */
  private async refresh(): Promise<void> {
    if (this.refreshing) return;
    this.refreshing = true;
    const jobId = this.jobId;
    try {
      const job = await this.repo.getJob(jobId);
      if (jobId !== this.jobId || this.state.status === "loading") return;
      if (job) this.setState({ status: "loaded", job });
    } catch {
      // Intentionally ignored; the next tick retries.
    } finally {
      this.refreshing = false;
      this.syncPolling();
    }
  }

  /**
   * Gated on disposal because every caller runs after an await: a fetch that
   * resolves once the sheet has closed would otherwise start a poller that can
   * never be stopped, the dispose callbacks having already run.
   */
  private syncPolling(): void {
    if (this.disposed) return;
    const { job } = this.state;
    if (job && !isTerminal(job.status)) {
      this.poller.start();
    } else {
      this.poller.stop();
    }
  }
}
